import React, { useState, useEffect, useContext } from 'react';
import {
	faDownload,
	faCheck,
	faChevronLeft,
	faChevronRight,
	faInbox
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Table } from "reactstrap";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import { format } from "date-fns";
import ReactPaginate from "react-paginate";
import { PostData, DownloadData } from '../../../services/FetchData';
import { ProductContext } from '../../../store';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import './AIAssistantProducts.scss';
import './AIAssistant.css';

const formatAnswerHtml = (answer = '') =>
	String(answer)
		.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
		.replace(/\n/g, '<br>')
		.replace(/•/g, '&bull;');

const shouldShowFullAnswerLink = (answer = '') => String(answer).trim().length > 120;

const AIAssistantResponses = () => {
	const { currentWebsiteId } = useContext(ProductContext);
	const [pageLoader, setPageLoader] = useState(true);
	const [responses, setResponses] = useState([]);
	const [totalResponses, setTotalResponses] = useState(0);
	const [currentPage, setCurrentPage] = useState(0);
	const [itemsPerPage] = useState(10);
	const [deleteModal, setDeleteModal] = useState(false);
	const [selectedResponse, setSelectedResponse] = useState(null);
	const [searchTerm, setSearchTerm] = useState(""); // Input field value (not applied until Apply Filters is clicked)
	const [appliedSearchTerm, setAppliedSearchTerm] = useState(""); // Actual search term used in API calls
	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const [viewAnswerResponse, setViewAnswerResponse] = useState(null);

	// Fetch responses from API
	useEffect(() => {
		const fetchResponses = async () => {
			const websiteId = currentWebsiteId || Cookies.get("_website_id");

			if (!websiteId) {
				console.error("No website ID found");
				toast.error("Website ID is missing. Please refresh the page.");
				setPageLoader(false);
				return;
			}

			setPageLoader(true);
			try {
				const postData = {
					websiteId: websiteId,
					page: currentPage + 1, // API expects 1-based page
					limit: itemsPerPage,
					searchTerm: appliedSearchTerm || ''
				};

				console.log("Fetching responses with data:", postData);

				const response = await PostData("ms1", "api/v1/aiassistant/responses", postData);

				console.log("API Response:", response);

				if (response !== "Invalid" && response !== undefined) {
					if (response.status === "success") {
						const responsesData = response.data.responses || [];

						// Format responses - handle status (default to 'pending' if not provided)
						const formattedResponses = responsesData.map(r => ({
							...r,
							status: r.status || 'pending', // Default status if not provided
							createdAt: r.created ? new Date(r.created) : new Date() // Use created date from backend
						}));

						setResponses(formattedResponses);
						setTotalResponses(response.data.total || 0);
					} else if (response.status === "failure" || response.status === "not_found_error") {
						console.error("Failed response:", response);
						const errorMessage = response.message?.message || response.message || "Failed to fetch responses";
						toast.error(errorMessage);
						setResponses([]);
						setTotalResponses(0);
					} else {
						console.error("Unexpected response format:", response);
						toast.error(response.message?.message || "Failed to fetch responses");
						setResponses([]);
						setTotalResponses(0);
					}
				} else {
					console.error("Invalid response:", response);
					toast.error("Invalid response from server");
					setResponses([]);
					setTotalResponses(0);
				}
			} catch (error) {
				console.error("Error fetching responses:", error);
				toast.error("An error occurred while fetching responses");
				setResponses([]);
				setTotalResponses(0);
			} finally {
				setPageLoader(false);
			}
		};

		fetchResponses();
	}, [currentPage, itemsPerPage, appliedSearchTerm, currentWebsiteId]);

	const handleApproveClick = async (responseId) => {
		// Optimistically update UI
		setResponses(prev => prev.map(r =>
			r.id === responseId ? { ...r, status: 'approved' } : r
		));

		// TODO: Add API call to approve response if backend endpoint is available
		// For now, just update local state
		console.log('Approved response:', responseId);
		toast.success('Response approved successfully');
	};

	const confirmDelete = async () => {
		if (selectedResponse) {
			// Optimistically remove from UI
			setResponses(prev => prev.filter(r => r.id !== selectedResponse.id));
			setTotalResponses(prev => prev - 1);
			setDeleteModal(false);
			setSelectedResponse(null);

			// TODO: Add API call to delete response if backend endpoint is available
			toast.success('Response deleted successfully');
		}
	};

	const handlePageClick = (data) => {
		setCurrentPage(data.selected);
	};

	// Handler for clear all filters
	const handleClearFilters = () => {
		setSearchTerm('');
		setAppliedSearchTerm('');
		setCurrentPage(0);
		setIsFilterOpen(false);
	};

	// Handler for apply filters
	const handleApplyFilters = () => {
		const trimmedSearchTerm = searchTerm.trim();
		setAppliedSearchTerm(trimmedSearchTerm);
		setCurrentPage(0);
		setIsFilterOpen(false);
	};

	const handleExportCSV = async () => {
		const websiteId = currentWebsiteId || Cookies.get("_website_id");

		if (!websiteId) {
			toast.error("Website ID is missing");
			return;
		}

		try {
			const csvContent = await DownloadData(
				'ms1',
				'api/v1/aiassistant/exportallairesponses',
				{
					websiteId,
					searchTerm: appliedSearchTerm || ''
				}
			);

			if (typeof csvContent !== 'string') {
				throw new Error('Unexpected response from export');
			}

			const filename = `ai_responses_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.csv`;
			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
			const link = document.createElement('a');
			const url = URL.createObjectURL(blob);
			link.setAttribute('href', url);
			link.setAttribute('download', filename);
			link.style.visibility = 'hidden';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);

			const lineCount = csvContent.split('\n').filter((l) => l.trim()).length;
			const rowCount = Math.max(0, lineCount - 1);
			toast.success(rowCount === 0 ? 'No responses to export' : `Exported ${rowCount} response(s)`);
		} catch (error) {
			console.error('Error exporting CSV:', error);
			toast.error(error.message || 'Failed to export CSV. Please try again.');
		}
	};


	return (
		<React.Fragment>
			<div className="ai-assistant-responses">
				<div className="d-flex justify-content-between align-items-start mb-4">
					<div>
						<h2 className="mb-1">AI Responses</h2>
						<p className="text-muted mb-0">Manage AI-generated responses</p>
					</div>
					<div className="d-flex gap-2">
						<Button
							className="filter-btn"
							color="outline-secondary"
							onClick={() => {
								if (!isFilterOpen) {
									setSearchTerm(appliedSearchTerm);
								}
								setIsFilterOpen(!isFilterOpen);
							}}
						>
							<i className="fa fa-filter"></i>
							Filter
						</Button>
						<Button
							color="outline-secondary"
							className="d-flex align-items-center export-button"
							onClick={handleExportCSV}
						>
							<FontAwesomeIcon icon={faDownload} className="me-2" />
							Export CSV
						</Button>
					</div>
				</div>

				{pageLoader ? (
					<DetailSkeleton />
				) : (
					<React.Fragment>
						<div className="table-responsive">
							<Table className="data-table">
								<thead>
									<tr>
										<th>SKU</th>
										<th>Product Name</th>
										<th>Question</th>
										<th>Answer</th>
										<th>Source</th>
										<th>Action</th>
									</tr>
								</thead>
								<tbody>
									{responses.length > 0 ? (
										responses.map((response) => (
											<tr key={response.id}>
												<td>
													<div className="searchItemStatusWrp">
														{response.sku}
													</div>
												</td>
												<td>
													<div className="searchItemStatusWrp question-text-cell">
														<div
															className="question-text question-text--clamped"
															title={response.productName}
														>
															{response.productName || '—'}
														</div>
													</div>
												</td>
												<td>
													<div className="searchItemStatusWrp question-text-cell">
														<div
															className="question-text question-text--clamped"
															title={response.question}
														>
															{response.question}
														</div>
													</div>
												</td>
												<td>
													<div className="searchItemStatusWrp answer-text-cell">
														<div
															className="answer-text answer-text--clamped"
															dangerouslySetInnerHTML={{
																__html: formatAnswerHtml(response.answer)
															}}
														/>
														{shouldShowFullAnswerLink(response.answer) && (
															<Button
																color="link"
																className="p-0 answer-view-btn"
																onClick={() => setViewAnswerResponse(response)}
															>
																View full answer
															</Button>
														)}
													</div>
												</td>
												<td>
													<div className="searchItemStatusWrp">
														<span
															className="badge source-badge"
														>
															{response.source}
														</span>
													</div>
												</td>
												<td>
													<div className="d-flex justify-content-center">
														{response.status === 'pending' ? (
															<Button
																size="sm"
																color="primary"
																className="d-flex align-items-center approve-button btn bg-primary"
																onClick={() => handleApproveClick(response.id)}
															>
																<FontAwesomeIcon icon={faCheck} className="me-1" />
																Approve
															</Button>
														) : (
															<Button
																size="sm"
																color="success"
																className="d-flex align-items-center approved-button"
																disabled
															>
																<FontAwesomeIcon icon={faCheck} className="me-1" />
																Approved
															</Button>
														)}
													</div>
												</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan={6} className="text-center p-5">
												<div className="d-flex flex-column align-items-center">
													<FontAwesomeIcon icon={faInbox} style={{ fontSize: '48px', color: '#dee2e6' }} />
													<p className="text-muted mt-3 mb-0">No Responses Found</p>
												</div>
											</td>
										</tr>
									)}
								</tbody>
							</Table>
						</div>
					</React.Fragment>
				)}

				{totalResponses > itemsPerPage && (
					<div className="d-flex justify-content-center mt-4">
						<ReactPaginate
							previousLabel={<FontAwesomeIcon icon={faChevronLeft} />}
							nextLabel={<FontAwesomeIcon icon={faChevronRight} />}
							pageCount={Math.ceil(totalResponses / itemsPerPage)}
							onPageChange={handlePageClick}
							forcePage={currentPage}
							containerClassName="pagination"
							previousLinkClassName="page-link"
							nextLinkClassName="page-link"
							pageLinkClassName="page-link"
							breakLinkClassName="page-link"
							activeClassName="active"
							disabledClassName="disabled"
						/>
					</div>
				)}

				{/* Filter Sidebar */}
				{isFilterOpen && (
					<div className="filter-sidebar-overlay" onClick={() => {
						setSearchTerm(appliedSearchTerm);
						setIsFilterOpen(false);
					}}>
						<div className="filter-sidebar" onClick={(e) => e.stopPropagation()}>
							<div className="filter-sidebar-header">
								<h5 className="filter-sidebar-title">Filter</h5>
								<Button
									className="close-filter-btn"
									color="link"
									onClick={() => {
										setSearchTerm(appliedSearchTerm);
										setIsFilterOpen(false);
									}}
								>
									<i className="fa fa-times"></i>
								</Button>
							</div>

							<div className="filter-sidebar-content">
								{/* Search Section */}
								<div className="filter-section">
									<label className="filter-label">Search</label>
									<Input
										type="text"
										placeholder="Search by question, answer, or SKU..."
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										className="filter-input"
									/>
								</div>
							</div>

							<div className="filter-sidebar-footer">
								<Button
									className="clear-all-btn"
									color="secondary"
									outline
									onClick={handleClearFilters}
									style={{
										flex: 1,
										marginRight: '8px'
									}}
								>
									Clear All
								</Button>
								<Button
									className="apply-filters-btn"
									color="primary"
									onClick={handleApplyFilters}
									style={{
										flex: 1
									}}
								>
									Apply Filters
								</Button>
							</div>
						</div>
					</div>
				)}

				{/* Full Answer Modal */}
				<Modal
					isOpen={!!viewAnswerResponse}
					toggle={() => setViewAnswerResponse(null)}
					size="lg"
					className="ai-response-answer-modal modelBodyBlock"
				>
					<ModalHeader toggle={() => setViewAnswerResponse(null)}>
						Answer
					</ModalHeader>
					<ModalBody>
						{viewAnswerResponse?.productName && (
							<p className="text-muted mb-2">
								<strong>Product:</strong> {viewAnswerResponse.productName}
							</p>
						)}
						{viewAnswerResponse?.sku && (
							<p className="text-muted mb-2">
								<strong>SKU:</strong> {viewAnswerResponse.sku}
							</p>
						)}
						<p className="mb-3">
							<strong>Question:</strong> {viewAnswerResponse?.question}
						</p>
						<div
							className="answer-text answer-text--full"
							dangerouslySetInnerHTML={{
								__html: formatAnswerHtml(viewAnswerResponse?.answer)
							}}
						/>
					</ModalBody>
					<ModalFooter>
						<Button color="primary" outline onClick={() => setViewAnswerResponse(null)}>
							Close
						</Button>
					</ModalFooter>
				</Modal>

				{/* Delete Confirmation Modal */}
				<Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
					<ModalHeader toggle={() => setDeleteModal(false)}>
						Delete Response
					</ModalHeader>
					<ModalBody>
						Are you sure you want to delete this AI response? This action cannot be undone.
					</ModalBody>
					<ModalFooter>
						<Button color="primary" outline onClick={() => setDeleteModal(false)}>
							Cancel
						</Button>
						<Button color="danger" onClick={confirmDelete}>
							Delete
						</Button>
					</ModalFooter>
				</Modal>
			</div>
		</React.Fragment>
	);
}

export default AIAssistantResponses;
